import { Injectable } from '@angular/core';
import fundTitleQuery from '@graphql/fund-header/fund-title.graphql';
import { Logger } from '@utils/logger';
import { Subject, Observable } from 'rxjs';
import { FundDetailService } from '@products/services/fund-detail.service';
import { Product, ShareClass } from '@models';
import {
  FundTitleState,
  ShareClassData,
  ProductDetailConfiguration,
  ConfigurationId,
  LiteratureDocumentData,
  FundShareClassId,
  FundHeaderLink,
  FundId,
  LiteratureDocType,
} from '@types';
import { SiteConfigService } from '@services/site-config.service';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { map, switchMap } from 'rxjs/operators';
import { stringCompare } from '@utils/sort-utils';
import { AppStateService } from '@services/app-state.service';
import { hasValue } from '@products/utils/mappers/mapper.utils';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import cloneDeep from 'lodash/cloneDeep';

const logger = Logger.getLogger('FundTitleService');

@Injectable({
  providedIn: 'root',
})
export class FundTitleService {
  private state: FundTitleState = { data: {} };
  private fundTitleStateSubject: Subject<FundTitleState>;
  private productDetailConfiguration: ProductDetailConfiguration;

  constructor(
    private fundDetailService: FundDetailService,
    private siteConfigService: SiteConfigService,
    private literatureDataService: LiteratureDataService,
    private appStateService: AppStateService,
    private literatureConfigService: LiteratureGlobalConfigService
  ) {
    this.fundTitleStateSubject = new Subject();
  }

  public get fundTitleState(): Observable<FundTitleState> {
    return this.fundTitleStateSubject.asObservable();
  }

  /**
   * Call register method of fund detail service.
   * @param fundDataServiceParams recieved as fund parameters
   */
  public populate(
    productDetailConfiguration: ProductDetailConfiguration
  ): void {
    this.productDetailConfiguration = productDetailConfiguration;
    const paramMap = new Map();
    this.state = { data: {} };
    this.fundDetailService
      .register(fundTitleQuery, {
        fundId: productDetailConfiguration.fundId,
        shareClassCode: productDetailConfiguration.shareClassCode,
      })
      .pipe(
        switchMap((product: Product) => {
          logger.debug(product);
          if (product) {
            this.mapFundTitleState(product, productDetailConfiguration.fundId);
          }
          paramMap
            .set('cache', 'true')
            .set('frkOneTISNumber', productDetailConfiguration.fundId)
            .set('shareClass', productDetailConfiguration.shareClassCode);
          if (this.siteConfigService.isSiteInternational()) {
            paramMap.set('fundUmbrella', product.fundUmbrella);
          }
          return this.literatureDataService.loadData(paramMap, true, true).pipe(
            map((litData) => {
              let primaryShareClassMapLiteratureData: LiteratureDocumentData;
              let primaryDoc: LiteratureDocumentData;
              let docTermsType;
              const language = this.siteConfigService
                .getPdsLanguage()
                .replace('_', '-');

              if (
                product.productType === ConfigurationId.SMA ||
                product?.investmentManager?.toLowerCase().includes('franklin')
              ) {
                const fsIndex = litData.document.findIndex(
                  (doc) => doc.dctermsType === LiteratureDocType.FACT_SHEET
                );

                if (fsIndex > -1) {
                  docTermsType = LiteratureDocType.FACT_SHEET;
                } else {
                  docTermsType = LiteratureDocType.PRODUCT_PROFILE;
                }
              } else {
                docTermsType =
                  product.productType === ConfigurationId.TIF
                    ? LiteratureDocType.PRODUCT_PROFILE
                    : LiteratureDocType.FACT_SHEET;
              }
              // to map primary share class literature data for FundId level
              litData.document.forEach((data) => {
                const shareClassArray = data.frkShareClassCode
                  ? data.frkShareClassCode.split(',').map((item) => item.trim())
                  : [];
                if (
                  shareClassArray.includes(this.state.data.PrimaryShareClasCode)
                ) {
                  primaryShareClassMapLiteratureData = cloneDeep(data);
                }
              });
              if (productDetailConfiguration.shareClassCode === 'Z') {
                primaryDoc = litData.document.find((data) => {
                  if (data?.frkShareClassCode) {
                    if (
                      data.dctermsType === docTermsType &&
                      data?.frkShareClassCode.match('Z|Advisor') &&
                      (this.siteConfigService.isSiteInternational()
                        ? data.dctermsLanguage?.includes(language)
                        : true)
                    ) {
                      return data;
                    } else if (
                      data.dctermsType === docTermsType &&
                      this.literatureConfigService
                        .getDocumentMappingConfiguration()
                        .get(LiteratureDocType.FACT_SHEET) === 'FUND_ID'
                    ) {
                      return primaryShareClassMapLiteratureData;
                    }
                  } else if (
                    data.dctermsType === docTermsType &&
                    (this.siteConfigService.isSiteInternational()
                      ? data.dctermsLanguage?.includes(language)
                      : true)
                  ) {
                    return data;
                  }
                });
              } else {
                primaryDoc = litData.document.find((data) => {
                  if (data?.frkShareClassCode) {
                    if (
                      data.dctermsType === docTermsType &&
                      (this.siteConfigService.isSiteInternational()
                        ? data.dctermsLanguage?.includes(language)
                        : true)
                    ) {
                      return this.checkShareClass(
                        data,
                        productDetailConfiguration.shareClassCode,
                        primaryShareClassMapLiteratureData
                      );
                    }
                  } else if (
                    data.dctermsType === docTermsType &&
                    (this.siteConfigService.isSiteInternational()
                      ? data.dctermsLanguage?.includes(language)
                      : true)
                  ) {
                    return data;
                  }
                });
              }
              this.setDownloadLink(primaryDoc);
              this.sendNext();
            })
          );
        })
      )
      .subscribe();
  }

  checkShareClass(
    data: LiteratureDocumentData,
    pdsShareClassCode: string,
    primarShareClassMappedData: LiteratureDocumentData
  ): LiteratureDocumentData {
    let shareClassArray = data.frkShareClassCode
      .split(',')
      .map((item) => item.trim());
    shareClassArray = this.mapRomanAndNumericShareClass(shareClassArray);
    if (shareClassArray.includes(pdsShareClassCode)) {
      return data;
    }
    // to tag pdf data on fund level
    else if (
      this.literatureConfigService
        .getDocumentMappingConfiguration()
        .get(LiteratureDocType.FACT_SHEET) === 'FUND_ID'
    ) {
      return primarShareClassMappedData;
    }
  }

  mapRomanAndNumericShareClass(shareClassArray: string[]): string[] {
    if (shareClassArray.includes('1') && !shareClassArray.includes('I')) {
      shareClassArray.push('I');
    }
    if (shareClassArray.includes('2') && !shareClassArray.includes('II')) {
      shareClassArray.push('II');
    }
    if (shareClassArray.includes('I') && !shareClassArray.includes('1')) {
      shareClassArray.push('1');
    }
    if (shareClassArray.includes('II') && !shareClassArray.includes('2')) {
      shareClassArray.push('2');
    }
    return shareClassArray;
  }

  private setDownloadLink(primaryDoc: LiteratureDocumentData) {
    if (this.siteConfigService.isSiteInternational()) {
      if (primaryDoc?.literatureHref) {
        if (this.isExternalLink(primaryDoc?.literatureHref)) {
          this.state.data.downloadLink = primaryDoc.literatureHref;
        } else {
          this.state.data.downloadLink = `${this.appStateService.getLiteratureDownloadBaseUrl()}${
            primaryDoc.literatureHref
          }`;
        }
      }
    } else {
      if (primaryDoc?.customFilePath && primaryDoc?.downloadUrl) {
        this.state.data.downloadLink = `${this.appStateService.getLiteratureDownloadUrl()}${
          primaryDoc.customFilePath
        }`;
      }
    }
  }

  private isExternalLink(link: string): boolean {
    return link ? (link.indexOf('https') !== -1 ? true : false) : false;
  }

  /**
   * Map the fund - fund header information response.
   * @param product received as server response.
   */
  private mapFundTitleState(product: Product, fundId: FundId) {
    this.state.isTemit = this.siteConfigService.isTemit();
    this.state.data.fundName = product.fundName;
    this.state.data.productType = product.productType;
    product.shareClasses.forEach((data) => {
      if (data.primaryShareClass) {
        this.state.data.PrimaryShareClasCode = data.shareClassCode;
      }
    });
    this.state.data.identifier = this.getIdentifier(product);
    if (!this.isSingleShareClassProduct(product)) {
      const shareClasses = this.sortData(product.shareClasses);
      this.state.data.shareClasses = shareClasses.map(
        (shareClass: ShareClass): ShareClassData => {
          const shareclassKey =
            fundId + '-' + shareClass.identifiers.shareClassCode;
          return {
            shareClassCode: shareClass.identifiers.shareClassCode,
            shareClassName: shareClass.shareClassName,
            shareClassCurrency: shareClass.shareClassCurrency,
            link: shareClass.link,
            isActive: this.siteConfigService.isActiveShareClass(
              shareclassKey as FundShareClassId
            ),
          };
        }
      );
      this.state.data.selectedShareClass = this.getSelectedShareClass(product);
    }
    // data needed to save/remove favorites
    this.state.data.selectedShareClassData = this.getSelectedShareClassData(
      product
    );
    this.state.data.downloadLabel =
      product.productType === ConfigurationId.TIF
        ? 'products.download-product-profile'
        : 'products.download-factsheet';

    if (this.siteConfigService.product?.general?.fundHeaderLinks) {
      this.state.data.fundHeaderLinks = this.getFundHeaderLinks(
        product,
        fundId
      );
    }
    this.sendNext();
  }

  /**
   * Get the Fund Header links for the current segment and investment vehicle.
   * @param product received as server response.
   */
  getFundHeaderLinks(product: Product, fundId: FundId): FundHeaderLink[] {
    const investmentVehicle = product.investmentVehicle;
    const result = this.siteConfigService.product.general.fundHeaderLinks?.filter(
      (link: FundHeaderLink) => {
        return (
          link.investmentVehicles?.includes(investmentVehicle) ||
          link.fundList?.includes(fundId)
        );
      }
    );
    return result;
  }

  /**
   * Sort the shareclass on basis of name
   * @param shareClassData is array of shareClass received from service.
   */
  sortData(shareClassData: ShareClass[]): ShareClass[] {
    return shareClassData.sort((current, next) => {
      return stringCompare(
        current.shareClassName,
        next.shareClassName,
        null,
        null,
        false
      );
    });
  }

  private isSingleShareClassProduct(product: Product): boolean {
    return (
      product.shareClasses.length === 0 ||
      (product.shareClasses.length === 1 &&
        product.shareClasses[0].isSingleShareClass)
    );
  }

  private getIdentifier(product: Product): string {
    const currentShareClass = product.shareClasses.find(
      (shareClass: ShareClass) => {
        return (
          shareClass.identifiers.shareClassCode ===
          this.productDetailConfiguration.shareClassCode
        );
      }
    );
    const keyIdentifier = this.siteConfigService.product?.general
      .keyIdentifier?.[product.productTaxonomy];

    // WDE-5664: Display 'ticker' in "CA-ETF" just like non-SINGLCLASS shareclasses.
    //           Issue is that for hybrid ETF share class, taxonomy comes as mutual fund from PDS.
    //           Which doesn't matches taxonomy as ETF from BR. Ex. 2545-ET1.
    if (
      currentShareClass.isEtfClass &&
      this.siteConfigService.getPdsCountry() === 'CA'
    ) {
      // ***** Here, we're hardcoding and using 'ticker' as the configuration, as no other work around. ******************
      // ***** We can't use 'keyIdentifier', as it won't be correct due to taxonomy mismatch is case of hybrid fund. *****
      return currentShareClass.identifiers.ticker;
    } else if (keyIdentifier) {
      if (
        currentShareClass &&
        keyIdentifier in currentShareClass.identifiers &&
        hasValue(currentShareClass.identifiers, keyIdentifier)
      ) {
        return currentShareClass.identifiers[keyIdentifier];
      }
    }
  }

  private getSelectedShareClass(product: Product): string {
    const selectedShareClass = product.shareClasses.find(
      (shareclass: ShareClass) => {
        return (
          shareclass.identifiers.shareClassCode ===
          this.productDetailConfiguration.shareClassCode
        );
      }
    );
    return this.generateShareClassLabel(selectedShareClass);
  }

  private getSelectedShareClassData(product: Product): ShareClassData {
    const selectedShareClass = product.shareClasses.find(
      (shareclass: ShareClass) => {
        return (
          shareclass.identifiers.shareClassCode ===
          this.productDetailConfiguration.shareClassCode
        );
      }
    );
    return {
      shareClassCode: selectedShareClass.identifiers.shareClassCode,
      shareClassName: selectedShareClass.shareClassName,
      link: selectedShareClass.link,
    };
  }

  private generateShareClassLabel(selShareClass: ShareClass): string {
    let output = '';
    if (this.isShareClassCurrencyDisplayed()) {
      output =
        selShareClass?.shareClassName +
        (selShareClass?.shareClassCurrency ? '-' : '') +
        selShareClass?.shareClassCurrency;
    } else {
      output = selShareClass?.shareClassName;
    }
    return output;
  }

  private isShareClassCurrencyDisplayed() {
    return this.siteConfigService.isCanada();
  }

  /**
   * Send update to the subscribed observer.
   */
  private sendNext() {
    this.fundTitleStateSubject.next(this.state);
  }
}
